/**
 * Demo API router — answers every `/api/**` request in-process so the CRM runs
 * with no Pages Functions behind it (TAB_ARCHITECTURE.md, "Direction").
 *
 * READS
 *   Backed by a snapshot table → served from it: rocks, teams, seats,
 *   meetings, knowledge, admin/table, field-status (clock snapshots), and the
 *   customer half of an SMS thread.
 *
 *   Only ever sourced from an external integration → answered by a SIMULATED
 *   generator in src/demo/simulated, so the screen is fully populated instead
 *   of rendering its disconnected state: QuickBooks Online (accounting, budget,
 *   storage billing), QuickBooks Time, Microsoft 365 calendar, GPS Insight
 *   dispatch + field status, RingCentral (text thread, call timing), Google
 *   drive time and the operations coaching grades. Every one of those payloads
 *   carries `simulated: true` plus a `demo_note` saying how it was derived, and
 *   calls `recordSimulatedHit()` so <SimulatedDataBanner /> names it on screen.
 *
 *   Still answered with the integration's own "not connected" shape, because
 *   nothing in the snapshot supports a credible figure: Encircle, RingCentral
 *   recordings/transcripts, Google geocoding, the NJ parcel lookup, mold
 *   technician availability, the alert-lead ingest and the signed-document gate.
 *
 * WRITES
 *   Acknowledged with `{ ok: true }` and NOT applied, except three that people
 *   will obviously try in a demo and would otherwise look silently broken —
 *   they are applied to the in-memory store so the UI reflects them until the
 *   page reloads:
 *     POST  /api/job-notes      → inserts a job_notes row
 *     POST  /api/todos          → inserts a user_todos row
 *     PATCH /api/todos/:id      → updates it;  DELETE removes it
 *     POST  /api/call-log       → patches the call_log row (lead marking)
 *
 *   Everything else is a no-op. A caller that expects a created id back
 *   (e.g. /api/jobs/create → navigate to the new job) will not get one; those
 *   get wired as each screen is rebuilt to spec.
 */
import { loadTable } from '~/demo/data';
import { supabase } from '~/lib/supabase';
import { DEMO_DISPLAY_NAME, DEMO_USER_ID } from '~/demo/client/auth';
import {
  recordSimulatedHit,
  simulatedAccounting, simulatedAccountingSync,
  simulatedBudget, simulatedBudgetSync,
  simulatedTime, simulatedTimeSync,
  simulatedStorageBilled, simulatedStorageBilledSync,
  simulatedGpsDispatch, simulatedFieldStatus, simulatedCalendar,
  simulatedCoaching, simulatedCoachingGenerate,
  simulatedDriveTime, simulatedRoundTrip,
  simulatedCallTiming, simulatedSmsThread,
  type BudgetMethod, type CoachingWindow,
} from '~/demo/simulated';
// Company-tab generators (Part 7). Only the parameterless ones are routed here;
// /api/qb-labor, /api/qb-labor-day and /api/qb-budget-detail take arguments the
// screens pass straight to the generator (see company/simulated/fetchOrSimulate).
import {
  simulatedEbitdaTrailing, simulatedJobPnl, simulatedPayments,
  simulatedInvoicedJobs, simulatedGmbReviews,
} from '~/components/dashboards/company/simulated/qbExtras';
import { simulatedAttendance } from '~/components/dashboards/company/simulated/labor';
import { simulatedHrDates } from '~/components/dashboards/company/simulated/hr';

export type Ctx = { method: string; path: string; url: URL; body: unknown };
type Handler = (ctx: Ctx) => Promise<unknown> | unknown;
type Rows = Record<string, unknown>[];

const JSON_HEADERS = { 'content-type': 'application/json' };

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), { status, headers: JSON_HEADERS });
}

/**
 * Wrap a simulated generator: record the hit (so the SIMULATED DATA banner can
 * name the integration on this screen) and return its payload unchanged.
 */
function simulated<T>(endpoint: string, make: (ctx: Ctx) => Promise<T> | T): Handler {
  return async (ctx: Ctx) => {
    recordSimulatedHit(endpoint);
    return make(ctx);
  };
}

function obj(b: unknown): Record<string, unknown> {
  return b !== null && typeof b === 'object' ? (b as Record<string, unknown>) : {};
}

// The fake client is typed as the real SupabaseClient; the demo router writes
// rows whose shape it knows from the server handlers, so relax the table typing.
const db = supabase as unknown as {
  from: (t: string) => {
    insert: (row: Record<string, unknown>) => {
      select: (c: string) => { single: () => PromiseLike<{ data: Record<string, unknown> | null; error: unknown }> };
    };
    update: (patch: Record<string, unknown>) => { eq: (c: string, v: unknown) => PromiseLike<{ error: unknown }> };
    delete: () => { eq: (c: string, v: unknown) => PromiseLike<{ error: unknown }> };
  };
};

// ───────────────────────────── reads ─────────────────────────────

const GET: Record<string, Handler> = {
  '/api/admin/table': async ({ url }) => {
    const t = url.searchParams.get('t') ?? '';
    const limit = Number(url.searchParams.get('limit') ?? 100);
    const offset = Number(url.searchParams.get('offset') ?? 0);
    const q = (url.searchParams.get('search') ?? '').trim().toLowerCase();
    let rows: Rows = await loadTable(t);
    if (q) {
      rows = rows.filter((r) =>
        Object.values(r).some((v) => typeof v === 'string' && v.toLowerCase().includes(q)));
    }
    return { ok: true, rows: rows.slice(offset, offset + limit), count: rows.length };
  },

  '/api/admin/sync-flag': () => ({ ok: true, flags: [] }),

  '/api/alert/sources': () => ({
    ok: true, ingest_configured: false, ai_qualifier_configured: false,
    sources: [], counties: [], feed_map: {},
  }),

  '/api/coaching/operations': simulated('/api/coaching/operations', ({ url }) =>
    simulatedCoaching((url.searchParams.get('window') ?? 'weekly') as CoachingWindow)),

  '/api/drive-time': simulated('/api/drive-time', ({ url }) => {
    const p = url.searchParams;
    const originLabel = p.get('originLat') || p.get('originId') ? 'Current location' : 'PDQ Headquarters';
    return simulatedDriveTime(
      { address: p.get('address'), city: p.get('city'), state: p.get('state'), zip: p.get('zip') },
      originLabel,
    );
  }),

  '/api/encircle/scope': ({ url }) => {
    const claim = url.searchParams.get('claim_id');
    return claim
      ? { ok: true, claim_id: claim, scope_rooms: [], all_rooms: [], claim_notes: [] }
      : { ok: true, jobs: [], total: 0, cached_meta: 0, uncached_meta: 0, claims_source: 'demo', claims_fetched_at: null };
  },
  '/api/encircle/notes': () => ({ ok: true, matched: false, claim_id: null, notes: [] }),

  // The people are the real time_clock_snapshots rows; the trucks they are
  // placed on come from the simulated GPS board, so this counts as simulated.
  '/api/field-status': simulated('/api/field-status', () => simulatedFieldStatus()),

  '/api/gps/dispatch': simulated('/api/gps/dispatch', ({ url }) =>
    simulatedGpsDispatch(url.searchParams.get('date') ?? undefined)),
  '/api/geocode': () => ({ ok: false, error: 'Geocoding is not connected in the demo build' }),

  '/api/knowledge': async () => ({
    ok: true, folders: await loadTable('knowledge_folders'), docs: await loadTable('knowledge_docs'),
  }),

  '/api/m365/calendar': simulated('/api/m365/calendar', ({ url }) => {
    const p = url.searchParams;
    return simulatedCalendar(p.get('team') ?? 'mine', {
      start: p.get('start') ?? undefined, end: p.get('end') ?? undefined,
    });
  }),

  '/api/meetings': async () => ({
    ok: true, meetings: await loadTable('meetings'), templates: await loadTable('meeting_templates'),
  }),

  '/api/mold/availability': () => ({
    ok: true, technician: null,
    reason: 'No technician calendar is connected in the demo build',
    timezone: 'America/New_York', days: [],
  }),

  '/api/property/lookup': () => ({ found: false, year_built: null, details: [], source: null, reason: 'lookup_failed' }),

  '/api/qb-accounting': simulated('/api/qb-accounting', () => simulatedAccounting()),
  '/api/qb-budget': simulated('/api/qb-budget', ({ url }) =>
    simulatedBudget((url.searchParams.get('method') ?? 'accrual') as BudgetMethod)),
  '/api/qb-time': simulated('/api/qb-time', () => simulatedTime()),
  '/api/qb-storage-billed': simulated('/api/qb-storage-billed', () => simulatedStorageBilled()),
  '/api/qb-invoices': () => ({ ok: true, fetched_at: Date.now(), matched_customers: [], invoices: [], source: 'cache' }),
  '/api/qb-ebitda-trailing': simulated('/api/qb-ebitda-trailing', () => simulatedEbitdaTrailing()),
  '/api/qb-job-pnl':         simulated('/api/qb-job-pnl', () => simulatedJobPnl()),
  '/api/qb-payments':        simulated('/api/qb-payments', () => simulatedPayments()),
  '/api/qb-invoiced-jobs':   simulated('/api/qb-invoiced-jobs', () => simulatedInvoicedJobs()),
  '/api/qb-attendance':      simulated('/api/qb-attendance', () => simulatedAttendance()),
  '/api/gmb-reviews':        simulated('/api/gmb-reviews', () => simulatedGmbReviews()),
  '/api/hr-dates':           simulated('/api/hr-dates', () => simulatedHrDates()),
  '/api/qb/status': () => ({ connected: false, expires_at: null, realm_id: null }),
  '/api/qb/connect': () => ({ ok: false, error: 'QuickBooks OAuth is not available in the demo build' }),

  // The Speed to Lead SCREEN reads call_log from the snapshot directly; this
  // endpoint carries the owner's timing model over those same rows.
  '/api/rc/speed-to-lead': simulated('/api/rc/speed-to-lead', () => simulatedCallTiming()),

  '/api/rc/transcript': () => ({
    ok: true, status: 'no_recording', turns: [], labels: {}, summary: null,
    callLogId: null, hasRecording: false, durationSec: null,
  }),
  '/api/rc/recording': () => json({ ok: false, error: 'not_found' }, 404),

  '/api/rocks': async () => ({
    ok: true, rocks: await loadTable('rocks'), milestones: await loadTable('rock_milestones'),
  }),

  '/api/seats': async () => ({
    ok: true, seats: await loadTable('org_seats'), assignments: await loadTable('seat_assignments'),
  }),

  '/api/sms/thread': simulated('/api/sms/thread', ({ url }) => {
    const p = url.searchParams;
    const id = p.get('customer_id') ?? p.get('customerId') ?? p.get('customer') ?? '';
    return simulatedSmsThread(id);
  }),

  '/api/teams': async () => ({
    ok: true, teams: await loadTable('teams'), members: await loadTable('team_members'),
  }),

  '/api/dates/signed-gate': () => ({
    ok: true, allowed: false, cos: null, work_auth: null,
    reason: 'Document detection is not available in the demo build',
  }),
};

// ──────────────────── writes applied to the store ────────────────────

const WRITE: Record<string, Handler> = {
  // ── Simulated integrations: the Refresh / Sync buttons on the screens above.
  // There is nothing to pull, so they re-derive and acknowledge.
  '/api/qb-accounting': simulated('/api/qb-accounting', () => simulatedAccountingSync()),
  '/api/qb-budget': simulated('/api/qb-budget', ({ body }) =>
    simulatedBudgetSync((obj(body).method as BudgetMethod) ?? 'accrual')),
  '/api/qb-time': simulated('/api/qb-time', () => simulatedTimeSync()),
  '/api/qb-storage-billed': simulated('/api/qb-storage-billed', () => simulatedStorageBilledSync()),
  '/api/field-status': simulated('/api/field-status', () => simulatedFieldStatus()),
  '/api/coaching/operations': simulated('/api/coaching/operations', ({ body }) => {
    const b = obj(body);
    return simulatedCoachingGenerate(
      (b.mode as 'grades' | 'summary') ?? 'grades',
      (b.window as CoachingWindow) ?? 'weekly',
    );
  }),
  // POST /api/drive-time is the Work Day Planner's office → stops → office trip.
  '/api/drive-time': simulated('/api/drive-time', ({ body }) => {
    const stops = obj(body).stops;
    return simulatedRoundTrip(Array.isArray(stops) ? (stops as Record<string, string>[]) : []);
  }),

  // Mirrors functions/api/job-notes.ts: body carries the row, server adds kind.
  '/api/job-notes': async ({ body }) => {
    const b = obj(body);
    const row = {
      job_id: b.job_id ?? null,
      body: String(b.body ?? ''),
      kind: String(b.kind ?? 'journal'),
      contact_kind: b.contact_kind ?? null,
      author_name: String(b.author_name ?? DEMO_DISPLAY_NAME),
      author_user_id: DEMO_USER_ID,
    };
    const { data } = await db.from('job_notes').insert(row).select('*').single();
    return { ok: true, id: data?.id ?? null, note: data };
  },

  // Mirrors functions/api/todos/index.ts.
  '/api/todos': async ({ body }) => {
    const b = obj(body);
    const row = {
      user_id: (b.assignee_user_id as string | undefined) || DEMO_USER_ID,
      team_id: b.team_id ?? null,
      title: String(b.title ?? '').trim(),
      detail: typeof b.detail === 'string' && b.detail.trim() ? b.detail.trim() : null,
      job_id: b.job_id ?? null,
      due_at: b.due_at ?? null,
      source: 'manual',
      is_done: false,
    };
    const { data } = await db.from('user_todos').insert(row).select('*').single();
    return { ok: true, id: data?.id ?? null, todo: data };
  },

  // Mirrors functions/api/call-log.ts: a patch keyed by id.
  '/api/call-log': async ({ body }) => {
    const b = obj(body);
    if (!b.id) return json({ ok: false, error: 'id required' }, 400);
    const patch: Record<string, unknown> = {};
    for (const k of ['is_lead', 'lead_status', 'scheduled_at', 'booked_override', 'sold_override', 'follow_up_rep_user_id', 'user_notes']) {
      if (k in b) patch[k] = b[k] ?? null;
    }
    // Clearing follow-up clears its rep in the same write, as the server does.
    if ('lead_status' in b && (b.lead_status ?? null) !== 'follow_up') patch.follow_up_rep_user_id = null;
    await db.from('call_log').update(patch).eq('id', b.id);
    return { ok: true };
  },
};

async function writeTodoById({ method, path, body }: Ctx): Promise<unknown> {
  const id = path.slice('/api/todos/'.length);
  if (method === 'DELETE') {
    await db.from('user_todos').delete().eq('id', id);
  } else {
    const patch = obj(body);
    await db.from('user_todos').update(patch).eq('id', id);
  }
  return { ok: true };
}

// ───────────────────────────── dispatch ─────────────────────────────

export async function route(ctx: Ctx): Promise<Response> {
  const { method, path } = ctx;
  try {
    // Sign-in goes through /api/login first; a non-200 takes auth.tsx's
    // existing fail-open path to the fake signInWithPassword, which succeeds.
    if (path === '/api/login') return json({ ok: false, error: 'not available in the demo build' }, 404);

    let handler: Handler | undefined;
    if (method === 'GET') {
      handler = GET[path];
    } else {
      handler = WRITE[path];
      if (!handler && path.startsWith('/api/todos/')) handler = writeTodoById;
    }

    if (!handler) {
      if (method === 'GET') console.warn(`[demo api] no handler for GET ${path} — answering { ok: true }`);
      return json({ ok: true });
    }

    const result = await handler(ctx);
    return result instanceof Response ? result : json(result);
  } catch (err) {
    console.error(`[demo api] ${method} ${path} threw`, err);
    return json({ ok: false, error: err instanceof Error ? err.message : String(err) }, 500);
  }
}
